import React, { useEffect, useState } from 'react';
import { AppProvider, useApp } from './core/AppContext';
import ModelEvalView from './features/model_eval/ModelEvalView';
import BenchmarkingView from './features/benchmarking/BenchmarkingView';
import TsaPersonaView from './features/tsa_persona/TsaPersonaView';
import LabVendingView from './features/lab_vending/LabVendingView';
import AiIntakeView from './features/ai_intake/AiIntakeView';
import AdminView from './features/admin/AdminView';
import SelfTestView from './features/self_test/SelfTestView';
import CouncilTracePanel from './features/model_eval/CouncilTracePanel';
import CouncilView from './features/council/CouncilView';
import ReviewQueueView from './features/approvals/ReviewQueueView';

// Shown in the content area when the user navigates (or is defaulted) to a
// menu they do not have the privilege to view. Deny-by-default: a newly added
// user sees no menus until an admin grants the matching view_* privilege.
function NoAccess() {
  return (
    <div style={{ maxWidth: '520px', margin: '60px auto', background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #dc3545', borderRadius: '8px', padding: '22px', color: '#721c24' }}>
      <h3 style={{ marginTop: 0, color: '#b00020' }}>No access to this area</h3>
      <p style={{ fontSize: '13px', color: '#555', margin: 0 }}>
        You don't have permission to view this menu. Ask an admin to grant the matching
        access privilege in <strong>Admin &rarr; Users</strong>.
      </p>
    </div>
  );
}

// Self-service password change, available to ANY logged-in user from the
// profile corner (sidebar footer). Self-contained: owns its modal open state
// and fields, posts to /api/change-password with the caller's bearer token.
function ChangePasswordButton({ getAuthHeaders }) {
  const [open, setOpen] = useState(false);
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setMsg('');
    if (newPw.length < 6) { setMsg('New password must be at least 6 characters.'); return; }
    if (newPw !== confirmPw) { setMsg('New passwords do not match.'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/change-password', {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_password: oldPw, new_password: newPw }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg('OK — password updated.');
        setOldPw(''); setNewPw(''); setConfirmPw('');
        setTimeout(() => { setOpen(false); setMsg(''); }, 1200);
      } else {
        setMsg((data && data.error) || 'Could not change password.');
      }
    } catch (e) {
      setMsg('Network error.');
    } finally {
      setBusy(false);
    }
  };

  const inp = { width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' };
  return (
    <>
      <button onClick={() => setOpen(true)} style={{ marginTop: '8px', marginLeft: '6px', backgroundColor: 'transparent', border: '1px solid #4a90d9', color: '#4a90d9', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Change Password</button>
      {open && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '22px', width: '340px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
            <h3 style={{ marginTop: 0, color: '#1a2a33' }}>Change Password</h3>
            <input type="password" placeholder="Current password" value={oldPw} onChange={e => setOldPw(e.target.value)} style={inp} />
            <input type="password" placeholder="New password (min 6 chars)" value={newPw} onChange={e => setNewPw(e.target.value)} style={inp} />
            <input type="password" placeholder="Confirm new password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} style={inp} />
            {msg && <div style={{ fontSize: '12px', margin: '6px 0', color: msg.indexOf('OK') === 0 ? '#155724' : '#721c24' }}>{msg}</div>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '10px' }}>
              <button onClick={() => { setOpen(false); setMsg(''); }} disabled={busy} style={{ padding: '6px 12px', border: '1px solid #ccc', background: '#f5f5f5', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={submit} disabled={busy} style={{ padding: '6px 12px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '4px', cursor: 'pointer' }}>{busy ? 'Saving...' : 'Update'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Shell() {
  const { can, evalResultModel, handleEvalJobDone, getAuthHeaders, fetchAdminConfig, fetchAdminUsers, getGovernanceAnalysis, handleLogin, handleLogout, loading, loginPass, loginUser, mainNav, renderModelScorecard, role, scorecard, selectedJustificationModel, setEvalResultModel, setLoginPass, setLoginUser, setMainNav, setSelectedJustificationModel, setSidebarCollapsed, sidebarCollapsed, statusMsg, token, username } = useApp();

  // Deny-by-default landing: after login, if the current menu isn't viewable by
  // this user, jump to the first menu they CAN see. Admins see everything, so
  // this is a no-op for them. Keeps a least-privilege user off a NoAccess screen.
  useEffect(() => {
    if (!token) return;
    const menus = [
      ['eval', 'view_model_eval'],
      ['approvals', 'view_model_eval'],
      ['benchmarking', 'view_benchmarking'],
      ['tsa', 'view_tsa'],
      ['vending', 'view_lab_vending'],
      ['intake', 'view_intake'],
      ['council', 'use_council'],
      ['selftest', 'manage_config'],
      ['admin', 'manage_users'],
    ];
    const viewable = (nav, priv) => {
      if (nav === 'admin') return can('view_admin') || can('manage_users') || can('manage_config') || can('view_audit');
      if (nav === 'council') return can('view_council') || can('use_council') || can('manage_config');
      if (nav === 'selftest') return can('view_selftest') || can('manage_config');
      return can(priv);
    };
    const currentOk = menus.some(([nav, priv]) => nav === mainNav && viewable(nav, priv));
    if (!currentOk) {
      const first = menus.find(([nav, priv]) => viewable(nav, priv));
      if (first) setMainNav(first[0]);
    }
    /* eslint-disable-next-line */
  }, [token, role, mainNav]);

  if (!token) {
    return (
      <div style={{ maxWidth: '420px', margin: '80px auto', fontFamily: 'Segoe UI, sans-serif', padding: '30px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <h2 style={{ marginTop: 0, color: '#0073bb', textAlign: 'center' }}>EEA AI Portal</h2>
        <p style={{ fontSize: '12px', color: '#888', textAlign: 'center', marginTop: '-10px', marginBottom: '20px' }}>Echostar Enterprise Architecture</p>
        
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Username</label>
            <input
              type="text"
              placeholder="e.g. admin or viewer"
              value={loginUser}
              onChange={(e) => setLoginUser(e.target.value)}
              required
              style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={loginPass}
              onChange={(e) => setLoginPass(e.target.value)}
              required
              style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
            />
          </div>
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', backgroundColor: '#0073bb', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}>
            {loading ? 'Authenticating...' : 'Sign In'}
          </button>
        </form>

        {statusMsg && <p style={{ color: '#d9534f', fontSize: '12px', textAlign: 'center', marginTop: '15px' }}>{statusMsg}</p>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Segoe UI, sans-serif', backgroundColor: '#f4f6f9' }}>
      
      {/* COLLAPSIBLE SIDEBAR NAVIGATION */}
      <div style={{
        width: sidebarCollapsed ? '60px' : '250px',
        backgroundColor: '#0f2027', color: '#fff', padding: '20px 0', flexShrink: 0,
        boxShadow: '2px 0 10px rgba(0,0,0,0.1)', transition: 'width 0.2s ease-in-out',
        position: 'relative'
      }}>
        <div style={{ padding: '0 15px 15px 15px', borderBottom: '1px solid #203a43', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!sidebarCollapsed && (
            <div>
              <h2 style={{ margin: 0, color: '#00bfff', fontSize: '20px', letterSpacing: '1px' }}>EEA</h2>
              <span style={{ fontSize: '11px', color: '#a0aec0', display: 'block', marginTop: '2px' }}>Echostar Enterprise Architecture</span>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{ backgroundColor: '#203a43', border: 'none', color: '#00bfff', borderRadius: '4px', padding: '6px', cursor: 'pointer', fontSize: '12px', margin: '0 auto' }}>
            {sidebarCollapsed ? '▶' : '◀'}
          </button>
        </div>

        <nav style={{ marginTop: '20px' }}>
          {can('view_model_eval') && (
          <div
            onClick={() => setMainNav('eval')}
            title="Global Model Evaluation (BETA)"
            style={{
              padding: '12px 15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
              backgroundColor: mainNav === 'eval' ? '#203a43' : 'transparent',
              borderLeft: mainNav === 'eval' ? '4px solid #00bfff' : '4px solid transparent',
              color: mainNav === 'eval' ? '#00bfff' : '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden'
            }}>
            🌐 {!sidebarCollapsed && <span>Global Model Eval <span style={{ fontSize: '9px', backgroundColor: '#17a2b8', color: '#fff', padding: '2px 5px', borderRadius: '8px' }}>BETA</span></span>}
          </div>
          )}

          {can('view_model_eval') && (
          <div
            onClick={() => setMainNav('approvals')}
            title="Model Governance — Approval Queue"
            style={{
              padding: '12px 15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
              backgroundColor: mainNav === 'approvals' ? '#203a43' : 'transparent',
              borderLeft: mainNav === 'approvals' ? '4px solid #00bfff' : '4px solid transparent',
              color: mainNav === 'approvals' ? '#00bfff' : '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden'
            }}>
            🛰️ {!sidebarCollapsed && <span>Approval Queue <span style={{ fontSize: '9px', backgroundColor: '#fd7e14', color: '#fff', padding: '2px 5px', borderRadius: '8px' }}>NEW</span></span>}
          </div>
          )}

          {can('view_benchmarking') && (
          <div
            onClick={() => setMainNav('benchmarking')}
            title="Benchmarking Platform (SOON)"
            style={{
              padding: '12px 15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
              backgroundColor: mainNav === 'benchmarking' ? '#203a43' : 'transparent',
              borderLeft: mainNav === 'benchmarking' ? '4px solid #00bfff' : '4px solid transparent',
              color: mainNav === 'benchmarking' ? '#00bfff' : '#a0aec0', whiteSpace: 'nowrap', overflow: 'hidden'
            }}>
            📊 {!sidebarCollapsed && <span>Benchmarking Platform <span style={{ fontSize: '9px', backgroundColor: '#ffc107', color: '#000', padding: '2px 5px', borderRadius: '8px' }}>SOON</span></span>}
          </div>
          )}

          {can('view_tsa') && (
          <div
            onClick={() => setMainNav('tsa')}
            title="TSA Persona (BETA)"
            style={{
              padding: '12px 15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
              backgroundColor: mainNav === 'tsa' ? '#203a43' : 'transparent',
              borderLeft: mainNav === 'tsa' ? '4px solid #00bfff' : '4px solid transparent',
              color: mainNav === 'tsa' ? '#00bfff' : '#a0aec0', whiteSpace: 'nowrap', overflow: 'hidden'
            }}>
            👤 {!sidebarCollapsed && <span>TSA Persona <span style={{ fontSize: '9px', backgroundColor: '#17a2b8', color: '#fff', padding: '2px 5px', borderRadius: '8px' }}>BETA</span></span>}
          </div>
          )}

          {can('view_lab_vending') && (
          <div
            onClick={() => setMainNav('vending')}
            title="Lab Vending (BETA)"
            style={{
              padding: '12px 15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
              backgroundColor: mainNav === 'vending' ? '#203a43' : 'transparent',
              borderLeft: mainNav === 'vending' ? '4px solid #00bfff' : '4px solid transparent',
              color: mainNav === 'vending' ? '#00bfff' : '#a0aec0', whiteSpace: 'nowrap', overflow: 'hidden'
            }}>
            🧪 {!sidebarCollapsed && <span>Lab Vending <span style={{ fontSize: '9px', backgroundColor: '#17a2b8', color: '#fff', padding: '2px 5px', borderRadius: '8px' }}>BETA</span></span>}
          </div>
          )}

          {can('view_intake') && (
          <div
            onClick={() => setMainNav('intake')}
            title="AI Intake (SOON)"
            style={{
              padding: '12px 15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
              backgroundColor: mainNav === 'intake' ? '#203a43' : 'transparent',
              borderLeft: mainNav === 'intake' ? '4px solid #00bfff' : '4px solid transparent',
              color: mainNav === 'intake' ? '#a0aec0' : '#a0aec0', whiteSpace: 'nowrap', overflow: 'hidden'
            }}>
            📥 {!sidebarCollapsed && <span>AI Intake <span style={{ fontSize: '9px', backgroundColor: '#ffc107', color: '#000', padding: '2px 5px', borderRadius: '8px' }}>SOON</span></span>}
          </div>
          )}

          {(can('view_council') || can('use_council') || can('manage_config')) && (
            <div
              onClick={() => setMainNav('council')}
              title="LLM Council Validation"
              style={{
                padding: '12px 15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
                backgroundColor: mainNav === 'council' ? '#203a43' : 'transparent',
                borderLeft: mainNav === 'council' ? '4px solid #00bfff' : '4px solid transparent',
                color: mainNav === 'council' ? '#00bfff' : '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden'
              }}>
              🧑‍⚖️ {!sidebarCollapsed && <span>Council Validation <span style={{ fontSize: '9px', backgroundColor: '#17a2b8', color: '#fff', padding: '2px 5px', borderRadius: '8px' }}>NEW</span></span>}
            </div>
          )}

          {(can('view_selftest') || can('manage_config')) && (
            <div
              onClick={() => setMainNav('selftest')}
              title="Self Test"
              style={{
                padding: '12px 15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
                backgroundColor: mainNav === 'selftest' ? '#203a43' : 'transparent',
                borderLeft: mainNav === 'selftest' ? '4px solid #00bfff' : '4px solid transparent',
                color: mainNav === 'selftest' ? '#00bfff' : '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden'
              }}>
              🧪 {!sidebarCollapsed && <span>Self Test</span>}
            </div>
          )}

          {(can('view_admin') || can('manage_users') || can('manage_config') || can('view_audit')) && (
            <div
              onClick={() => { setMainNav('admin'); fetchAdminConfig(); if (can('manage_users')) fetchAdminUsers(); }}
              title="Admin"
              style={{
                padding: '12px 15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
                backgroundColor: mainNav === 'admin' ? '#203a43' : 'transparent',
                borderLeft: mainNav === 'admin' ? '4px solid #00bfff' : '4px solid transparent',
                color: mainNav === 'admin' ? '#00bfff' : '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden'
              }}>
              ⚙️ {!sidebarCollapsed && <span>Admin</span>}
            </div>
          )}
        </nav>

        {!sidebarCollapsed && (
          <div style={{ padding: '15px', marginTop: 'auto', fontSize: '12px', borderTop: '1px solid #203a43', position: 'absolute', bottom: 0, width: '220px' }}>
            <div style={{ color: '#cbd5e0', marginBottom: '4px' }}>User: <strong>{username}</strong></div>
            <div style={{ color: '#00bfff', fontSize: '11px', fontWeight: 'bold' }}>Role: {role.toUpperCase()}</div>
            <button onClick={handleLogout} style={{ marginTop: '8px', backgroundColor: 'transparent', border: '1px solid #e53e3e', color: '#e53e3e', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Sign Out</button>
            <ChangePasswordButton getAuthHeaders={getAuthHeaders} />
          </div>
        )}
      </div>

      {/* MAIN CONTENT CONTAINER */}
      <div style={{ flex: 1, padding: '25px', overflowY: 'auto' }}>
        {mainNav === 'eval' && (can('view_model_eval') ? <ModelEvalView /> : <NoAccess />)}
        {mainNav === 'approvals' && (can('view_model_eval') ? <ReviewQueueView /> : <NoAccess />)}
        {mainNav === 'benchmarking' && (can('view_benchmarking') ? <BenchmarkingView /> : <NoAccess />)}
        {mainNav === 'tsa' && (can('view_tsa') ? <TsaPersonaView /> : <NoAccess />)}
        {mainNav === 'vending' && (can('view_lab_vending') ? <LabVendingView /> : <NoAccess />)}
        {mainNav === 'intake' && (can('view_intake') ? <AiIntakeView /> : <NoAccess />)}
        {mainNav === 'admin' && ((can('view_admin') || can('manage_users') || can('manage_config') || can('view_audit')) ? <AdminView /> : <NoAccess />)}
        {mainNav === 'council' && ((can('view_council') || can('use_council') || can('manage_config')) ? <CouncilView /> : <NoAccess />)}
        {mainNav === 'selftest' && ((can('view_selftest') || can('manage_config')) ? <SelfTestView /> : <NoAccess />)}
      </div>

      {/* GOVERNANCE JUSTIFICATION MODAL */}
      {evalResultModel && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}
             onClick={() => setEvalResultModel(null)}>
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '22px', maxWidth: (evalResultModel.__job && evalResultModel.__showCouncil) ? '1200px' : '680px', width: '94%', maxHeight: '90vh', overflowY: 'auto' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, color: '#0f2027' }}>Evaluation Result</h3>
              <button onClick={() => setEvalResultModel(null)}
                      style={{ border: 'none', background: '#eee', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}>Close</button>
            </div>
            <p style={{ fontSize: '13px', color: '#0073bb', fontWeight: 'bold', wordBreak: 'break-all' }}>{evalResultModel.model_id}</p>
            {evalResultModel.__loading && <p style={{ color: '#666' }}>{evalResultModel.__showCouncil
              ? 'Running live evaluation — the council\'s steps appear below as they happen…'
              : 'Running live evaluation (golden test suite on the real model)… this can take up to a minute.'}</p>}
            {evalResultModel.__error && <p style={{ color: '#b00020' }}>{evalResultModel.__error}</p>}
            {!evalResultModel.__loading && !evalResultModel.__error &&
              renderModelScorecard(evalResultModel, evalResultModel.compliance_status !== 'ECHOSTAR_APPROVED')}
            {evalResultModel.__job && (
              <CouncilTracePanel jobId={evalResultModel.__job} headers={getAuthHeaders} onDone={handleEvalJobDone}
                compact={!evalResultModel.__showCouncil} />
            )}
          </div>
        </div>
      )}

      {selectedJustificationModel && (() => {
        const gov = getGovernanceAnalysis(selectedJustificationModel);
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '25px', maxWidth: '650px', width: '90%', maxHeight: '85vh', overflowY: 'auto' }}>
              <h3 style={{ marginTop: 0, color: '#0f2027' }}>Governance Approval Justification & Policy Analysis</h3>
              <p style={{ fontSize: '13px', color: '#0073bb', fontWeight: 'bold' }}>Model ID: {selectedJustificationModel.model_id || selectedJustificationModel.id}</p>
              
              <div style={{ backgroundColor: '#f8f9fa', borderLeft: `5px solid ${gov.badgeColor}`, padding: '12px', borderRadius: '4px', marginBottom: '15px', fontSize: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: gov.badgeColor, marginBottom: '4px' }}>
                  POLICY RECOMMENDATION: {gov.recommendation}
                </div>
                <strong>Governing Policy Rule:</strong> {gov.ruleTitle}<br/>
                <strong>Current Status:</strong> {gov.statusLabel}<br/>
                <strong>Target Virtual Alias:</strong> {selectedJustificationModel.assigned_virtual_model || 'echostar/smart-v1'}
              </div>

              <div style={{ backgroundColor: '#eef6fc', padding: '12px', borderRadius: '4px', marginBottom: '15px', fontSize: '12px', border: '1px solid #b6d4fe' }}>
                <strong style={{ color: '#084298' }}>EchoStar AI Security Rationale:</strong>
                <p style={{ margin: '4px 0 0 0', color: '#333' }}>{gov.why}</p>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '15px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#eef6fc', textAlign: 'left' }}>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>Evaluation Gate</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>Status</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>EEA Compliance Metrics</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Gates use MEASURED data only: a live golden test-suite run (Evaluate).
                    const jsc = selectedJustificationModel.scorecard || {};
                    const live = (jsc.evaluation_mode || selectedJustificationModel.evaluation_mode) === 'LIVE';
                    const golden = live && jsc.golden_suite_benchmark_score != null ? Number(jsc.golden_suite_benchmark_score) : null;
                    const thr = Number(jsc.approved_threshold ?? 80);
                    const p95 = live && jsc.p95_latency_ms != null ? Number(jsc.p95_latency_ms) : null;
                    const cell = { padding: '8px', border: '1px solid #ddd' };
                    const st = (ok) => ({ ...cell, fontWeight: 'bold', color: ok === null ? '#856404' : ok ? '#28a745' : '#dc3545' });
                    const gOk = golden === null ? null : golden >= thr;
                    const lOk = p95 === null ? null : p95 <= 500;
                    return [
                      <tr key="gate1">
                        <td style={cell}>Gate 1: Golden Suite Accuracy</td>
                        <td style={st(gOk)}>{gOk === null ? 'NOT EVALUATED' : gOk ? 'PASSED' : 'FAILED'}</td>
                        <td style={cell}>{golden === null ? 'No live golden test-suite run yet. Click Evaluate to run it on Bedrock.' : `Scored ${golden}% on the golden test suite (live run; threshold ${thr}%).`}</td>
                      </tr>,
                      <tr key="gate2">
                        <td style={cell}>Gate 2: Latency &amp; Cost SLA</td>
                        <td style={st(lOk)}>{lOk === null ? 'NOT EVALUATED' : lOk ? 'PASSED' : 'REVIEW'}</td>
                        <td style={cell}>{p95 === null ? 'No measured latency yet (needs a live Evaluate).' : `Measured P95 latency ${p95} ms (target 500 ms).`}</td>
                      </tr>
                    ];
                  })()}
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>Gate 3: Security & Origin Audit</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', color: gov.recommendation === 'PROHIBITED' ? '#dc3545' : '#28a745', fontWeight: 'bold' }}>
                      {gov.recommendation === 'PROHIBITED' ? 'FAILED (POLICY)' : 'PASSED'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {gov.recommendation === 'PROHIBITED' ? 'Failed foreign origin / web interface check.' : 'US-hosted provider terms & PII redaction verified.'}
                    </td>
                  </tr>
                </tbody>
              </table>

              <button
                onClick={() => setSelectedJustificationModel(null)}
                style={{ padding: '8px 16px', backgroundColor: '#0073bb', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', width: '100%' }}>
                Close Justification
              </button>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
